/** @fileoverview Shares and applies the persisted Fabrik theme across the application. */

import { useEffect, useState } from 'react';

export type Theme = 'light' | 'dark' | 'system';

const THEME_KEY = 'fabrik-theme';
const THEME_EVENT = 'fabrik-theme-change';

const readTheme = (): Theme => {
  if (typeof window === 'undefined') return 'light';
  const value = window.localStorage.getItem(THEME_KEY);
  return value === 'light' || value === 'dark' || value === 'system' ? value : 'light';
};

/** Applies the selected theme and keeps multiple consumers in sync. */
export function useTheme() {
  const [theme, setTheme] = useState<Theme>(readTheme);

  useEffect(() => {
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const applyTheme = () => {
      const dark = theme === 'dark' || (theme === 'system' && media.matches);
      document.documentElement.classList.toggle('dark', dark);
      document.documentElement.style.colorScheme = dark ? 'dark' : 'light';
    };

    applyTheme();
    if (theme !== 'system') return;
    media.addEventListener('change', applyTheme);
    return () => media.removeEventListener('change', applyTheme);
  }, [theme]);

  useEffect(() => {
    const syncTheme = () => setTheme(readTheme());
    window.addEventListener(THEME_EVENT, syncTheme);
    window.addEventListener('storage', syncTheme);
    return () => {
      window.removeEventListener(THEME_EVENT, syncTheme);
      window.removeEventListener('storage', syncTheme);
    };
  }, []);

  const chooseTheme = (value: Theme) => {
    window.localStorage.setItem(THEME_KEY, value);
    setTheme(value);
    window.dispatchEvent(new Event(THEME_EVENT));
  };

  return { theme, chooseTheme };
}
